import { useEffect, useState } from "react";
import { connectivityApi, intelligenceApi } from "@/lib/api-client";
import { Mail, Globe, Zap, RefreshCw, Unlink, CheckCircle2, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { relativeTime } from "@/lib/format";

export default function ConnectivityHub() {
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [appPw, setAppPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [findings, setFindings] = useState([]);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    try {
      const s = await connectivityApi.status();
      setStatus(s);
      const f = await intelligenceApi.findings(20);
      setFindings(f);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!email || !appPw) return toast.error("Email and Google App Password required");
    setBusy(true);
    try {
      await connectivityApi.connectGmail(email, appPw);
      toast.success("Gmail linked");
      setAppPw("");
      load();
    } catch { toast.error("Connect failed. Check email + app password."); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Gmail?")) return;
    await connectivityApi.disconnectGmail();
    toast.success("Gmail disconnected");
    load();
  };

  const scan = async () => {
    setBusy(true);
    try {
      const r = await connectivityApi.scanGmail();
      toast.success(`Scanned ${r.scanned} emails, ${r.processed} flagged`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setBusy(false); }
  };

  const intelScan = async () => {
    setScanning(true);
    try {
      const r = await intelligenceApi.scan(null, 3);
      toast.success(`Web scan complete · ${r.hits} signals across ${r.ventures_scanned} ventures`);
      load();
    } catch { toast.error("Web scan failed"); }
    finally { setScanning(false); }
  };

  const testSend = async () => {
    setBusy(true);
    try {
      const r = await connectivityApi.testSendGmail();
      if (r.delivered) toast.success(`Test email delivered to ${r.to} via ${r.gateway}`);
      else toast.error(`Not delivered — ${r.error}`);
    } catch { toast.error("Test send failed"); }
    finally { setBusy(false); }
  };

  const gmailConnected = status?.gmail?.connected;

  return (
    <div className="space-y-6" data-testid="connectivity-hub">
      <div>
        <div className="uc-chip mb-2"><Zap size={11} className="text-amber-400"/> CONNECTIVITY HUB</div>
        <h3 className="font-serif-display text-xl">External Data Fusion</h3>
        <p className="text-white/50 text-xs mt-1">Link business Gmail + toggle Global Intelligence scans. Findings flow into System Memory.</p>
      </div>

      {/* Gmail */}
      <div className="p-4 border border-white/10 bg-black/40" data-testid="gmail-section">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={14} className="text-red-400"/>
          <span className="font-mono-uc text-[11px] uppercase tracking-widest text-white/70">Business Gmail</span>
          {gmailConnected && (
            <span className="uc-chip ml-auto">
              <CheckCircle2 size={10} className="text-emerald-400"/>
              <span className="text-emerald-400">Connected</span>
            </span>
          )}
        </div>

        {gmailConnected ? (
          <div>
            <div className="text-sm text-white/85 mb-1" data-testid="gmail-connected-email">{status.gmail.email}</div>
            <div className="font-mono-uc text-[10px] text-white/40 mb-3">
              Last scan: {status.gmail.last_scan_at ? relativeTime(status.gmail.last_scan_at) : "never"}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button data-testid="gmail-scan-btn" onClick={scan} disabled={busy} className="uc-btn-amber rounded-none h-9 text-xs">
                {busy ? <Loader2 className="animate-spin mr-1" size={12}/> : <RefreshCw size={12} className="mr-1"/>}
                Scan Now
              </Button>
              <Button data-testid="gmail-test-send-btn" onClick={testSend} disabled={busy} variant="ghost" className="rounded-none h-9 text-xs border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                <Mail size={11} className="mr-1"/> Send Test Email
              </Button>
              <Button data-testid="gmail-disconnect-btn" onClick={disconnect} variant="ghost" className="rounded-none h-9 text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10">
                <Unlink size={11} className="mr-1"/> Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div>
              <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50 block mb-1">Gmail Address</Label>
              <Input
                data-testid="gmail-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com"
                className="h-9 rounded-none bg-black border-white/10 text-sm"
              />
            </div>
            <div>
              <Label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50 block mb-1">Google App Password</Label>
              <Input
                data-testid="gmail-pw-input"
                type="password"
                value={appPw}
                onChange={(e) => setAppPw(e.target.value)}
                placeholder="xxxx xxxx xxxx xxxx"
                className="h-9 rounded-none bg-black border-white/10 font-mono-uc text-sm"
              />
              <div className="text-[10px] text-white/40 mt-1">
                Create at <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-amber-400 hover:underline">myaccount.google.com/apppasswords</a> · 16-char code
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <Button data-testid="gmail-connect-btn" onClick={connect} disabled={busy} className="uc-btn-amber rounded-none h-9 text-xs flex-1">
                {busy ? <Loader2 className="animate-spin" size={12}/> : "Connect Gmail"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Global Intelligence */}
      <div className="p-4 border border-white/10 bg-black/40" data-testid="intel-section">
        <div className="flex items-center gap-2 mb-3">
          <Globe size={14} className="text-blue-400"/>
          <span className="font-mono-uc text-[11px] uppercase tracking-widest text-white/70">Global Intelligence Scan</span>
          <span className="uc-chip ml-auto">
            <CheckCircle2 size={10} className="text-emerald-400"/>
            <span className="text-emerald-400">Enabled</span>
          </span>
        </div>
        <p className="text-[11px] text-white/50 mb-3">Scout + Sentinel query the live web (DuckDuckGo) for news and risk signals on each venture using their metadata identifiers.</p>
        <Button data-testid="intel-scan-btn" onClick={intelScan} disabled={scanning} className="uc-btn-amber rounded-none h-9 text-xs w-full">
          {scanning ? <Loader2 className="animate-spin mr-1" size={12}/> : <Search size={12} className="mr-1"/>}
          Run Web Intelligence Scan
        </Button>
      </div>

      {/* Findings */}
      {findings.length > 0 && (
        <div data-testid="findings-list">
          <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60 mb-2">
            Recent Findings ({findings.length})
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto uc-scroll">
            {findings.map((f) => (
              <div key={f.id} className="p-2 border border-white/5 bg-black text-[11px]" data-testid={`finding-${f.id}`}>
                <div className="font-mono-uc text-[9px] uppercase tracking-wider text-white/40 mb-0.5">
                  {f.tags?.join(" · ") || f.type} · {relativeTime(f.created_at)}
                </div>
                <div className="text-white/80">{f.content?.slice(0, 200)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
